"use client";
import Link from "next/link";
import React, { useState } from "react";
import Logo from "../../../../public/assets/logo.png";
import Image from "next/image";
import { BiMenuAltRight, BiChevronDown, BiChevronUp } from "react-icons/bi";
import { MenuItems, MenuItems2 } from "../MenuItem";
import { BsX } from "react-icons/bs";

const ToolBar = ({
  setDrawerOpen,
}: {
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  return (
    <nav className="shadow bg-white py-2 flex items-center justify-between px-2 sticky top-0 w-full z-50">
      <Link href="/">
        <Image
          src={Logo}
          alt="logo"
          className="w-[45%] md:w-[20%] bg-white xl:rounded-full p-1"
        />
      </Link>

      <BiMenuAltRight
        size={200}
        onClick={() => setDrawerOpen(true)}
        className="cursor-pointer h-[5vh]"
      />
    </nav>
  );
};

// ✅ Helper to handle internal vs external links
const SmartLink = ({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  const isExternal = href.startsWith("http");
  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
};

const MobileHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  const handleDrawerClose = () => setDrawerOpen(false);

  const toggleSubMenu = (id: string) => {
    setExpandedMenu((prev) => (prev === id ? null : id));
  };

  return (
    <>
      <ToolBar drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />

      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/80 bg-opacity-50 z-50"
          onClick={handleDrawerClose}
        />
      )}

      <div
        className={`fixed top-0 left-0 h-full w-[90%] bg-white z-50 shadow-lg transform transition-transform duration-300 ease-in-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="bg-gray-200 py-4 flex items-center justify-between px-2">
          <Image src={Logo} alt="logo" className="w-[40%]" />

          <BsX
            size={35}
            onClick={handleDrawerClose}
            className="cursor-pointer text-red-600"
          />
        </div>

        <ul className="flex flex-col p-4 h-[90vh] overflow-y-auto">
          {MenuItems.map((m) => (
            <li key={m.id} className="w-full">
              <div className="flex justify-between items-center">
                <SmartLink
                  href={m.href ?? "/error"}
                  onClick={handleDrawerClose}
                  className="text-md p-4 sm:py-3 text-gray-700 tracking-[0.2em] font-medium hover:text-primary-main transition w-full block"
                >
                  {m.name}
                </SmartLink>
                {m.submenu && (
                  <button
                    onClick={() => toggleSubMenu(m.id)}
                    className="text-gray-700 focus:outline-none"
                    aria-label={
                      expandedMenu === m.id
                        ? `Collapse ${m.name} menu`
                        : `Expand ${m.name} menu`
                    }
                    aria-expanded={expandedMenu === m.id}
                    aria-controls={`submenu-${m.id}`}
                  >
                    {expandedMenu === m.id ? (
                      <BiChevronUp size={20} />
                    ) : (
                      <BiChevronDown size={20} />
                    )}
                  </button>
                )}
              </div>
              {m.submenu && expandedMenu === m.id && (
                <ul className="pl-4 bg-gray-100">
                  {m.submenu.map((sub) => (
                    <li key={sub.id}>
                      <SmartLink
                        href={sub.href}
                        onClick={handleDrawerClose}
                        className="text-sm p-3 sm:py-2 text-gray-700 tracking-[0.2em] font-medium hover:text-primary-main transition block"
                      >
                        {sub.name}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}

          {MenuItems2.map((m) => (
            <li key={m.id} className="w-full">
              <div className="flex justify-between items-center">
                <SmartLink
                  href={m.href ?? "/error"}
                  onClick={handleDrawerClose}
                  className="text-md p-4 sm:py-3 text-gray-700 tracking-[0.2em] font-medium hover:text-primary-main transition w-full block"
                >
                  {m.name}
                </SmartLink>
                {m.submenu && (
                  <button
                    onClick={() => toggleSubMenu(m.id)}
                    className="text-gray-700 focus:outline-none"
                    aria-label={
                      expandedMenu === m.id
                        ? `Collapse ${m.name} menu`
                        : `Expand ${m.name} menu`
                    }
                    aria-expanded={expandedMenu === m.id}
                    aria-controls={`submenu-${m.id}`}
                  >
                    {expandedMenu === m.id ? (
                      <BiChevronUp size={20} />
                    ) : (
                      <BiChevronDown size={20} />
                    )}
                  </button>
                )}
              </div>
              {m.submenu && expandedMenu === m.id && (
                <ul className="pl-4 bg-gray-100">
                  {m.submenu.map((sub) => (
                    <li key={sub.id}>
                      <SmartLink
                        href={sub.href}
                        onClick={handleDrawerClose}
                        className="text-sm p-3 sm:py-2 text-gray-700 tracking-[0.2em] font-medium hover:text-primary-main transition block"
                      >
                        {sub.name}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default MobileHeader;
